import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { enums } from '@daycal/shared';
import { api } from '../lib/apiClient.js';
import { FiTrash2 } from 'react-icons/fi';

const CHOICE_TYPES = new Set(['select', 'radio', 'checkbox']);
const ALL_SERVICES = 'All Services & Consultation Types';

const emptyForm = (eventTypeId = '') => ({
  eventTypeId,
  label: '',
  input_type: 'text',
  required: false,
  options: '',
  placeholder: '',
  helpText: '',
});

export default function FormBuilder() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialEventTypeId = searchParams.get('eventTypeId') || '';

  const { data, isLoading } = useQuery({ queryKey: ['form-questions'], queryFn: api.listFormQuestions });
  const { data: etData } = useQuery({ queryKey: ['event-types'], queryFn: api.listEventTypes });
  const [form, setForm] = useState(() => emptyForm(initialEventTypeId));
  const [error, setError] = useState('');

  const eventTypes = etData?.event_types || [];

  // "All Services" (empty value) + one entry per event type. Label→id lookup is
  // handled by CustomSelect via option objects.
  const serviceOptions = [
    { label: ALL_SERVICES, value: '' },
    ...eventTypes.map((et) => ({ label: et.title, value: et.id })),
  ];
  const eventTitleById = Object.fromEntries(eventTypes.map((et) => [et.id, et.title]));

  const create = useMutation({
    mutationFn: () => {
      const payload = {
        event_type_id: form.eventTypeId || null,
        label: form.label,
        input_type: form.input_type,
        required: form.required,
      };
      if (CHOICE_TYPES.has(form.input_type)) {
        payload.options = form.options.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        if (form.placeholder.trim()) payload.placeholder = form.placeholder.trim();
        if (form.helpText.trim()) payload.help_text = form.helpText.trim();
      }
      return api.createFormQuestion(payload);
    },
    onSuccess: () => {
      // Keep the currently selected service so the host can add several in a row.
      setForm((f) => emptyForm(f.eventTypeId));
      setError('');
      qc.invalidateQueries({ queryKey: ['form-questions'] });
    },
    onError: (err) => setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deleteFormQuestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-questions'] }),
  });

  if (isLoading) return <PageSkeleton />;
  const questions = data?.form_questions || [];

  return (
    <div className="animate-dc-rise mx-auto max-w-6xl space-y-6">
      {/* Top Form Creator Card */}
      <div className="rounded-dc border border-slate-200/80 bg-white p-6 md:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-5 mb-6">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Custom Booking Form Questions</h1>
              <span className="rounded-full bg-brand-50 border border-brand-200 px-2.5 py-0.5 text-xs font-bold text-brand-700 shadow-xs">
                {questions.length} Active
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Add extra questions for specific booking types or all services to collect details before appointments.
            </p>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          <label className="block relative z-30">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Apply to service / event *</span>
            <CustomSelect
              value={form.eventTypeId}
              options={serviceOptions}
              onChange={(val) => setForm((f) => ({ ...f, eventTypeId: val }))}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Question / field label *</span>
            <input 
              value={form.label} 
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="e.g., What is your main area of concern?" 
              className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs" 
            />
          </label>

          <label className="block relative z-20">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Field input type *</span>
            <CustomSelect
              value={form.input_type}
              options={enums.FORM_INPUT_TYPE}
              onChange={(val) => setForm((f) => ({ ...f, input_type: val }))}
            />
          </label>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          {CHOICE_TYPES.has(form.input_type) ? (
            <label className="block md:col-span-2">
              <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Options (comma-separated)</span>
              <input 
                value={form.options} 
                onChange={(e) => setForm((f) => ({ ...f, options: e.target.value }))}
                placeholder="Option 1, Option 2, Option 3" 
                className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs" 
              />
            </label>
          ) : (
            <>
              <label className="block">
                <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Placeholder text</span>
                <input 
                  value={form.placeholder} 
                  onChange={(e) => setForm((f) => ({ ...f, placeholder: e.target.value }))}
                  placeholder="e.g., Please be specific..." 
                  className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs" 
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Help / instruction text</span>
                <input 
                  value={form.helpText} 
                  onChange={(e) => setForm((f) => ({ ...f, helpText: e.target.value }))}
                  placeholder="e.g., Visible below the question field" 
                  className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs" 
                />
              </label>
            </>
          )}
        </div>

        {error && <p className="mt-4 text-xs font-semibold text-rose-600">{error}</p>}

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 pt-6">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className={`grid h-5 w-5 place-items-center rounded-dc-sm border transition-colors ${form.required ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300 bg-white group-hover:border-slate-400'}`}>
              <input type="checkbox" className="hidden" checked={form.required} onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))} />
              {form.required && <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
            </div>
            <span className="text-xs font-semibold text-slate-700">Mandatory Question (Required to complete booking)</span>
          </label>

          <button 
            onClick={() => create.mutate()} 
            disabled={create.isPending || !form.label} 
            className="mt-5 sm:mt-0 flex h-10 items-center justify-center rounded-dc-sm bg-slate-900 px-6 text-xs font-semibold text-white shadow-xs transition-all hover:bg-brand-600 active:scale-95 disabled:opacity-50 cursor-pointer"
          >
            {create.isPending ? <Spinner className="!h-4 !w-4 !border-t-white" /> : 'Add Form Question'}
          </button>
        </div>
      </div>

      {/* Bottom Table Card */}
      <div className="rounded-dc border border-slate-200/80 bg-white p-6 md:p-8 shadow-xs min-h-[350px]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-lg font-bold text-slate-900">Active Consultation Questions</h2>
          <span className="text-xs text-slate-400 font-semibold">{questions.length} total fields</span>
        </div>
        
        <div className="overflow-x-auto rounded-dc-sm border border-slate-200/80">
          <table className="w-full text-left text-xs text-slate-600">
            <thead className="border-b border-slate-200/80 bg-slate-50/80 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3.5 font-semibold">Question Label</th>
                <th className="px-5 py-3.5 font-semibold">Assigned Consultation</th>
                <th className="px-5 py-3.5 font-semibold">Input Type</th>
                <th className="px-5 py-3.5 font-semibold text-center">Required</th>
                <th className="px-5 py-3.5 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {questions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-12 text-center text-xs text-slate-400">
                    No custom form questions added yet.
                  </td>
                </tr>
              ) : (
                questions.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-bold text-slate-900">{q.label}</td>
                    <td className="px-5 py-3.5 text-slate-500 font-medium">{q.event_type_id ? (eventTitleById[q.event_type_id] || 'Specific Service') : ALL_SERVICES}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-full bg-brand-50 border border-brand-200 px-2.5 py-0.5 text-[10px] font-bold text-brand-700">
                        {q.input_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {q.required ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200/80 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700">Required</span>
                      ) : (
                        <span className="text-slate-400 text-[11px]">Optional</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button 
                        onClick={() => remove.mutate(q.id)} 
                        title="Delete question"
                        className="inline-flex items-center justify-center rounded-full p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CustomSelect({ options, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Accept either plain strings or { label, value } objects.
  const items = options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));
  const selected = items.find((it) => it.value === value);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`w-full h-10 flex items-center justify-between rounded-dc-sm border border-slate-300/90 px-3.5 text-xs font-medium outline-none transition-all cursor-pointer shadow-2xs ${
          disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-800 hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10'
        }`}
      >
        <span className="truncate">{selected?.label || placeholder}</span>
        <svg className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180 text-brand-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1.5 w-full rounded-dc border border-slate-200/80 bg-white py-1.5 shadow-xl max-h-52 overflow-y-auto custom-scrollbar animate-dc-rise">
          {items.map((it) => (
            <button
              key={it.value}
              type="button"
              onClick={() => {
                onChange(it.value);
                setOpen(false);
              }}
              className={`w-full px-3.5 py-2 text-left text-xs transition-colors hover:bg-brand-600 hover:text-white cursor-pointer ${value === it.value ? 'bg-brand-50 font-bold text-brand-700' : 'text-slate-700 font-medium'}`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
